<template>
  <section class="benefits" :class="{ 'benefits--custom': content }">
    <div class="benefits__container">
      <h2>{{ benefitsContent.heading }}</h2>
      <p>{{ benefitsContent.description }}</p>
      <div class="benefits__body">
        <div id="why-drct-benefits" class="benefits__list">
          <div
            v-for="(item, index) in items"
            v-show="showMore || !hasHiddenItems || index < 3"
            :key="item.title"
            class="benefit__item"
          >
            <div class="benefit__icons" aria-hidden="true">
              <div
                v-for="(icon, iconIndex) in item.icons"
                :key="`${item.title}-${iconIndex}`"
                class="benefit__icon"
              >
                <img v-if="item.iconSrc" :src="item.iconSrc" width="36" height="36" alt="" />
                <svg v-else width="36" height="36" viewBox="0 0 256 256">
                  <rect width="256" height="256" rx="49" fill="#2a8aea" fill-opacity=".12" />
                  <template v-if="icon && icon.type === 'globeSlashClean'">
                    <defs>
                      <mask
                        :id="globeSlashMaskId(index, iconIndex)"
                        x="0"
                        y="0"
                        width="256"
                        height="256"
                        maskUnits="userSpaceOnUse"
                      >
                        <rect width="256" height="256" fill="white" />
                        <path
                          d="M48,208,208,48"
                          fill="none"
                          stroke="black"
                          stroke-linecap="round"
                          stroke-width="57.4"
                        />
                      </mask>
                    </defs>
                    <g transform="translate(128,128) scale(0.56) translate(-128,-128)">
                      <g
                        :mask="`url(#${globeSlashMaskId(index, iconIndex)})`"
                        fill="none"
                        stroke="#2a8aea"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="16"
                      >
                        <circle cx="128" cy="128" r="88" />
                        <ellipse cx="128" cy="128" rx="34" ry="88" />
                        <path d="M40,128H216" />
                      </g>
                      <path
                        d="M48,208,208,48"
                        fill="none"
                        stroke="#2a8aea"
                        stroke-linecap="round"
                        stroke-width="16"
                      />
                    </g>
                  </template>
                  <g
                    v-else-if="icon && icon.type === 'stroke'"
                    transform="translate(128,128) scale(0.56) translate(-128,-128)"
                    fill="none"
                    stroke="#2a8aea"
                    :stroke-width="icon.strokeWidth || 16"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      v-for="(path, pathIndex) in icon.paths"
                      :key="`${item.title}-${iconIndex}-${pathIndex}`"
                      :d="path"
                    />
                  </g>
                  <g v-else transform="translate(128,128) scale(0.56) translate(-128,-128)">
                    <path fill="#2a8aea" :d="icon" />
                  </g>
                </svg>
              </div>
            </div>
            <div class="benefit__text">
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
            </div>
          </div>

          <div v-if="benefitsContent.wide" class="benefit__item benefit__item--wide">
            <div class="benefit__visual">
              <svg
                v-for="(icon, index) in wideIcons"
                :key="index"
                width="170"
                height="170"
                viewBox="-24 -24 304 304"
                aria-hidden="true"
              >
                <circle cx="128" cy="128" r="152" fill="#2a8aea" fill-opacity=".04" />
                <circle cx="128" cy="128" r="110" fill="#2a8aea" fill-opacity=".07" />
                <g transform="translate(128,128) scale(0.44) translate(-128,-128)">
                  <path fill="#2a8aea" :d="icon" />
                </g>
              </svg>
            </div>
            <div class="benefit__text">
              <h3>{{ benefitsContent.wide.title }}</h3>
              <p>{{ benefitsContent.wide.description }}</p>
            </div>
          </div>
        </div>
        <div v-if="hasHiddenItems" class="benefits__toggle-wrap">
          <button
            type="button"
            class="benefits__toggle"
            aria-controls="why-drct-benefits"
            :aria-expanded="showMore"
            @click="showMore = !showMore"
          >
            {{ showMore ? $t('hideAllFunctionality') : $t('showAllFunctionality') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { resolveIconReview } from './page-icon-review.js';
import { CARD_ICON_OPTIONS } from './WhatYouGain.vue';

const BENEFIT_ICONS = [
  'M230.91,172A8,8,0,0,1,228,182.91l-96,56a8,8,0,0,1-8.06,0l-96-56A8,8,0,0,1,36,169.09l92,53.65,92-53.65A8,8,0,0,1,230.91,172ZM220,121.09l-92,53.65L36,121.09A8,8,0,0,0,28,134.91l96,56a8,8,0,0,0,8.06,0l96-56A8,8,0,1,0,220,121.09ZM24,80a8,8,0,0,1,4-6.91l96-56a8,8,0,0,1,8.06,0l96,56a8,8,0,0,1,0,13.82l-96,56a8,8,0,0,1-8.06,0l-96-56A8,8,0,0,1,24,80Zm23.88,0L128,126.74,208.12,80,128,33.26Z',
  'M208,40H48A24,24,0,0,0,24,64V176a24,24,0,0,0,24,24H208a24,24,0,0,0,24-24V64A24,24,0,0,0,208,40Zm8,136a8,8,0,0,1-8,8H48a8,8,0,0,1-8-8V64a8,8,0,0,1,8-8H208a8,8,0,0,1,8,8Zm-48,48a8,8,0,0,1-8,8H96a8,8,0,0,1,0-16h64A8,8,0,0,1,168,224Z',
  'M239.18,97.26A16.38,16.38,0,0,0,224.92,86l-59-4.76L143.14,26.15a16.36,16.36,0,0,0-30.27,0L90.11,81.23,31.08,86a16.46,16.46,0,0,0-9.37,28.86l45,38.83L53,211.75a16.38,16.38,0,0,0,24.5,17.82L128,198.49l50.53,31.08A16.4,16.4,0,0,0,203,211.75l-13.76-58.07,45-38.83A16.43,16.43,0,0,0,239.18,97.26Zm-15.34,5.47-48.7,42a8,8,0,0,0-2.56,7.91l14.88,62.8a.37.37,0,0,1-.17.48c-.18.14-.23.11-.38,0l-54.72-33.65a8,8,0,0,0-8.38,0L69.09,215.94c-.15.09-.19.12-.38,0a.37.37,0,0,1-.17-.48l14.88-62.8a8,8,0,0,0-2.56-7.91l-48.7-42c-.12-.1-.23-.19-.13-.5s.18-.27.33-.29l63.92-5.16A8,8,0,0,0,103,91.86l24.62-59.61c.08-.17.11-.25.35-.25s.27.08.35.25L153,91.86a8,8,0,0,0,6.75,4.92l63.92,5.16c.15,0,.24,0,.33.29S224,102.63,223.84,102.73Z',
  'M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z',
  'M232,208a8,8,0,0,1-8,8H32a8,8,0,0,1-8-8V48a8,8,0,0,1,16,0V156.69l50.34-50.35a8,8,0,0,1,11.32,0L128,132.69,180.69,80H160a8,8,0,0,1,0-16h40a8,8,0,0,1,8,8v40a8,8,0,0,1-16,0V91.31l-58.34,58.35a8,8,0,0,1-11.32,0L96,123.31l-56,56V200H224A8,8,0,0,1,232,208Z',
  'M117.25,157.92a60,60,0,1,0-66.5,0A95.83,95.83,0,0,0,3.53,195.63a8,8,0,1,0,13.4,8.74,80,80,0,0,1,134.14,0,8,8,0,0,0,13.4-8.74A95.83,95.83,0,0,0,117.25,157.92ZM40,108a44,44,0,1,1,44,44A44.05,44.05,0,0,1,40,108Zm210.14,98.7a8,8,0,0,1-11.07-2.33A79.83,79.83,0,0,0,172,168a8,8,0,0,1,0-16,44,44,0,1,0-16.34-84.87,8,8,0,1,1-5.94-14.85,60,60,0,0,1,55.53,105.64,95.83,95.83,0,0,1,47.22,37.71A8,8,0,0,1,250.14,206.7Z',
];

export default {
  props: {
    wideIconReview: {
      type: Object,
      default: null,
    },
    iconReview: {
      type: Array,
      default: () => [],
    },
    content: {
      type: Object,
      default: null,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMore: false,
    };
  },
  computed: {
    wideIcons() {
      return resolveIconReview(
        [
          'M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z',
        ],
        this.wideIconReview
      );
    },
    defaultBenefits() {
      return {
        heading: this.$t('home.why.heading'),
        description: this.$t('home.why.description'),
        items: [1, 2, 3, 4, 5, 6].map((n) => ({
          title: this.$t(`home.why.item${n}Title`),
          description: this.$t(`home.why.item${n}Desc`),
        })),
        wide: {
          title: this.$t('home.why.wideTitle'),
          description: this.$t('home.why.wideDesc'),
        },
      };
    },
    benefitsContent() {
      return this.content || this.defaultBenefits;
    },
    items() {
      return this.benefitsContent.items.map((item, index) => {
        const fallbackIcon = item.icon || BENEFIT_ICONS[item.iconIndex ?? index];
        const icon = CARD_ICON_OPTIONS[item.iconKey] || fallbackIcon;
        const iconOptions = (item.iconOptions || []).map(
          (option) => CARD_ICON_OPTIONS[option] || option
        );

        return {
          ...item,
          icon,
          icons: resolveIconReview(
            item.replaceIconOptions ? iconOptions : [icon, ...iconOptions],
            this.iconReview[index]
          ),
        };
      });
    },
    hasHiddenItems() {
      return this.collapsible && this.items.length > 3;
    },
  },
  methods: {
    globeSlashMaskId(index, iconIndex) {
      return `globe-slash-mask-${this._uid}-${index}-${iconIndex}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits {
  background-image: linear-gradient(0deg, rgba(42, 138, 234, 0), rgba(42, 138, 234, 0.2));
  padding-bottom: 80px;
  padding-top: 80px;
}

.benefits__container {
  margin: 0 auto;
  width: 1010px;

  > h2 {
    color: #1b2733;
    font-size: 31px;
    font-weight: 700;
    text-align: center;
  }

  > p {
    color: #1b2733;
    font-size: 14px;
    line-height: 22px;
    margin-bottom: 50px;
    margin-top: 12px;
    text-align: center;
  }
}

.benefits__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 40px;
}

.benefits__toggle-wrap {
  text-align: center;
}

.benefits__toggle {
  padding: 10px 12px;
  border: 0;
  background: transparent;
  color: #2a8aea;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.6px;
  line-height: 1.4;
  opacity: 0.8;
  text-transform: uppercase;

  &:hover,
  &:active {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid #2a8aea;
    outline-offset: 4px;
  }
}

.benefit__item {
  width: 100%;
  max-width: none;
  margin-bottom: 40px;
  min-width: 0;
}

.benefit__icons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.benefit__icon {
  display: block;
  flex: 0 0 36px;
  line-height: 0;
}

.benefit__text {
  h3 {
    white-space: normal;
    color: #1b2733;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
  }

  p {
    color: rgba(27, 39, 51, 0.9);
    font-size: 14px;
    line-height: 1.57;
    margin-top: 6px;
    opacity: 0.9;
  }
}

.benefit__item--wide {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 0;
  background: linear-gradient(135deg, rgba(42, 138, 234, 0.13) 0%, rgba(42, 138, 234, 0.04) 100%);
  border-radius: 16px;
  padding: 0;
  margin-top: 20px;
  margin-bottom: 0;
  overflow: hidden;

  .benefit__text {
    flex: 1;
    padding: 32px 40px;

    h3 {
      font-size: 16px;
      margin-top: 0;
      margin-bottom: 8px;
    }
    p {
      max-width: 480px;
    }
  }
}

.benefit__visual {
  flex: 0 0 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

@media (max-width: 1024px) {
  .benefits__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 32px;
  }
  .benefits__container {
    width: 100%;
    padding: 0 32px;
  }
  .benefit__item--wide {
    flex-direction: column;
    align-items: flex-start;
  }
  .benefit__item--wide .benefit__text {
    padding: 0 32px 28px;
    flex: none;
  }
  .benefit__visual {
    flex: 0 0 auto;
    padding: 24px 24px 0;
    justify-content: flex-start;
  }
}

@media (max-width: 767px) {
  .benefits__list {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .benefits__container {
    padding: 0 20px;

    > h2 {
      line-height: 1.4;
    }
  }
  .benefit__text h3 {
    line-height: 1.4;
  }
  .benefit__item--wide {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  .benefit__item--wide .benefit__text {
    width: 100%;
    padding: 0 24px 24px;
    flex: none;
    text-align: center;
  }
  .benefit__visual {
    flex: 0 0 auto;
    padding: 24px 24px 0;
    justify-content: center;
  }
}

@media (max-width: 413px) {
  .benefits__container {
    padding: 0 12px;
  }
}
</style>
